import type {
  ForwardRefExoticComponent,
  FunctionComponent,
  ReactNode,
  RefAttributes,
} from 'react';

import { Trans } from '@lingui/react/macro';
import {
  ArrowsClockwiseIcon,
  NotePencilIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import type { IconProps } from '@remarkable/ark-icons';
import {
  CloudUpload,
  LayoutLaying,
  Lens,
  ShieldCheck,
  Template,
} from '@remarkable/ark-icons';
import {
  ConvertToNotebook,
  HandwrittenSearch,
  SendToSlack,
  Templates,
  UnlimitedCloudStorage,
} from '@remarkable/ark-icons/pictograms';
import type { Pictogram } from './pictogram.types';

type Icon =
  | ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>
  | FunctionComponent<IconProps>;

export interface ValuePropItem {
  title: ReactNode;
  id: string;
  icon: Icon;
  pictogram?: Pictogram;
  description?: ReactNode;
  badge?: ReactNode;
}

export const valueProps = {
  enterprise: {
    unlimitedCloudStorage: {
      title: 'Unlimited cloud storage',
      id: 'unlimitedCloudStorage',
      icon: CloudUpload,
    },
    automaticSync: {
      title: 'Automatic sync',
      id: 'automaticSync',
      icon: ArrowsClockwiseIcon,
    },
    createAndEditInOurApps: {
      title: 'Create and edit in our apps',
      id: 'createAndEditInOurApps',
      icon: NotePencilIcon,
    },
    remarkableMethods: {
      title: 'Templates and workbooks from reMarkable Methods',
      id: 'remarkableMethods',
      icon: LayoutLaying,
    },
  },

  connectWithSeats: {
    unlimitedCloudStorage: {
      title: 'Unlimited cloud storage',
      id: 'unlimitedCloudStorage',
      icon: CloudUpload,
    },
    automaticSync: {
      title: 'Automatic sync',
      id: 'automaticSync',
      icon: ArrowsClockwiseIcon,
    },
    createAndEditInOurApps: {
      title: 'Create and edit in our apps',
      id: 'createAndEditInOurApps',
      icon: NotePencilIcon,
    },
    remarkableProtectionPlan: {
      title: 'reMarkable Protection Plan',
      id: 'remarkableProtectionPlan',
      icon: ShieldCheck,
    },
  },

  connect: {
    exclusiveTemplates: {
      title: <Trans>Access exclusive templates</Trans>,
      id: 'exclusiveTemplates',
      icon: Template,
    },
    unlimitedCloudStorage: {
      title: <Trans>Unlimited cloud storage</Trans>,
      id: 'unlimitedCloudStorage',
      icon: CloudUpload,
    },
    aiConversionAndSharing: {
      title: (
        <span>
          <Trans>AI conversion and sharing</Trans>{' '}
          <SparkleIcon className="-mt-2 ml-2 inline" weight="fill" size={16} />
        </span>
      ),
      id: 'aiConversionAndSharing',
      icon: SparkleIcon,
    },
    handwritingSearch: {
      title: <Trans>Handwriting search</Trans>,
      id: 'handwritingSearch',
      icon: Lens,
    },
  },
  connectCancellation: {
    exclusiveTemplates: {
      title: <Trans>Exclusive templates from reMarkable Methods</Trans>,
      id: 'exclusiveTemplates',
      icon: Template,
      pictogram: Templates,
      description: (
        <Trans>
          Get templates and workbooks from a community of expert thinkers, on
          reMarkable Methods.
        </Trans>
      ),
    },
    aiConversionAndSharing: {
      title: <Trans>AI conversion and sharing</Trans>,
      id: 'aiConversionAndSharing',
      icon: SparkleIcon,
      pictogram: ConvertToNotebook,
      description: (
        <Trans>
          Seamlessly convert handwriting and sketches from your paper tablet,
          and watch them appear perfectly formatted as a flexible online note,
          or a Slack canvas.
        </Trans>
      ),
    },
    unlimitedCloudStorage: {
      title: <Trans>Unlimited cloud storage</Trans>,
      id: 'unlimitedCloudStorage',
      icon: CloudUpload,
      pictogram: UnlimitedCloudStorage,
      description: (
        <Trans>
          Connect gives you unlimited, secure cloud storage for all your
          documents. Always accessible and ready to use on your reMarkable paper
          tablet.
        </Trans>
      ),
    },
    handwritingSearch: {
      title: <Trans>Search all your notes, handwritten or typed</Trans>,
      id: 'handwritingSearch',
      icon: Lens,
      pictogram: HandwrittenSearch,
      description: (
        <Trans>
          Search the contents of both handwritten and typed notes, documents,
          and books, making it easier than ever to find exactly what you&apos;re
          looking for.
        </Trans>
      ),
    },
  },
  connectDetailed: {
    exclusiveTemplates: {
      title: <Trans>Access exclusive templates</Trans>,
      id: 'exclusiveTemplates',
      icon: Template,
      pictogram: Templates,
      description: (
        <Trans>
          Get templates and workbooks from a community of expert thinkers, on
          reMarkable Methods.
        </Trans>
      ),
    },
    aiConversionAndSharing: {
      title: <Trans>AI conversion and sharing</Trans>,
      id: 'aiConversionAndSharing',
      icon: SparkleIcon,
      pictogram: SendToSlack,
      description: (
        <Trans>
          Seamlessly convert handwriting and sketches from your paper tablet,
          and watch them appear perfectly formatted as a flexible online note,
          or a Slack canvas.
        </Trans>
      ),
    },
    unlimitedCloudStorage: {
      title: <Trans>Unlimited cloud storage</Trans>,
      id: 'unlimitedCloudStorage',
      icon: CloudUpload,
      pictogram: UnlimitedCloudStorage,
      description: (
        <Trans>
          Connect gives you unlimited, secure cloud storage for all your
          documents. Always accessible and ready to use on your reMarkable paper
          tablet.
        </Trans>
      ),
    },
    handwritingSearch: {
      title: <Trans>Handwriting search</Trans>,
      id: 'handwritingSearch',
      icon: Lens,
      pictogram: HandwrittenSearch,
      description: (
        <Trans>
          Search the contents of both handwritten and typed notes, documents,
          and books, making it easier than ever to find exactly what you&apos;re
          looking for.
        </Trans>
      ),
    },
  },
} satisfies Record<string, Record<string, ValuePropItem>>;
